import useUiNotification from '~/composables/useUiNotification';
import { useUser } from '~/composables/custom/useUser/useUser';
import { useBFF } from '~/composables/useBFF/useBFF';
import type { Address } from '~/types/GraphQL';

export const useLoyalty = () => {
  const { $me } = useBFF();
  const { send } = useUiNotification();
  const { load } = useUser();

  const result = shallowRef<unknown>({});
  const address = shallowRef<Address>({} as Address);
  const loading = ref(false);
  const error = ref(false);

  const transactionsCount = ref(0);
  const isTransactionsCountVisible = ref(true);
  const loadingTransactionsCount = ref(false);

  const register = async ({ cardNumber, newRegistration }: { cardNumber?: string; newRegistration?: boolean }) => {
    loading.value = true;
    error.value = false;

    try {
      const { data } = await $me.applyLoyaltyMembership(cardNumber, newRegistration);

      result.value = data;
      address.value = data?.addressInCRM || {};
      await load();
      return data;
    }
    catch {
      error.value = true;
      send({
        message: 'Register unsuccessful. Please contact customer service.',
        type: 'danger',
        icon: 'error',
      });
    }
    finally {
      loading.value = false;
    }

    return undefined;
  };

  const unregister = async () => {
    loading.value = true;
    error.value = false;

    try {
      const { data } = await $me.unregisterLoyaltyMembership();
      result.value = data;
      await load();
    }
    catch {
      error.value = true;
      send({
        message: 'Unregister unsuccessful. Please contact customer service.',
        type: 'danger',
      });
    }
    finally {
      loading.value = false;
    }

    return result.value;
  };

  const synchronize = async () => {
    try {
      await $me.synchronizeUserWithCRM();
    }
    catch {
      send({
        message: 'Sync address for Loyalty program unsuccessful. Please contact customer service.',
        type: 'danger',
      });
    }
  };

  const getCRMAddress = async () => {
    loading.value = true;
    error.value = false;

    try {
      const { data } = await $me.getAddressFromCRM();

      address.value = data;
      return address.value;
    }
    catch {
      error.value = true;
      send({
        message: 'Sorry! At the moment it\'s not possible to get this information. Please try again later',
        type: 'danger',
      });
    }
    finally {
      loading.value = false;
    }
  };

  const loadTransactionCount = async () => {
    loadingTransactionsCount.value = true;
    isTransactionsCountVisible.value = true;
    try {
      const { data } = await $me.userTransactionCount();
      transactionsCount.value = data;
    }
    catch {
      isTransactionsCountVisible.value = false;
    }
    finally {
      loadingTransactionsCount.value = false;
    }
  };

  return {
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    address: computed(() => address.value),
    loadTransactionCount,
    register,
    unregister,
    synchronize,
    getCRMAddress,
    transactionsCount: computed(() => transactionsCount.value),
    loadingTransactionsCount: computed(() => loadingTransactionsCount.value),
    isTransactionsCountVisible: computed(() => isTransactionsCountVisible.value),
  };
};
